import { queryOptions } from "@tanstack/react-query";
import { DateTime, Interval } from "luxon";
import { z } from "zod";

import { graphqlClient } from "@/api/graphql";
import {
  cropUnitSchema,
  traderSchema,
} from "@/api/types/digitalnisklady.cz/types";
import { currencyFormatter } from "@/common/services/formatter";
import { graphql } from "@/generated/digitalnisklady.cz";
import {
  ContractFieldsFragment,
  ContractStatus,
} from "@/generated/digitalnisklady.cz/graphql";

import { newestFirst } from "./utils";

// eslint-disable-next-line lingui/no-unlocalized-strings
graphql(`
  fragment ContractFields on Contract {
    marketPrice {
      quarter {
        id
        name
      }
      trader {
        id
        name
        logo
      }
    }
    confirmation {
      id
      completed
      documents {
        name
        url
      }
    }
    externalNumber
    id
    amount
    offer {
      crop {
        name(locale: $locale)
        unit
      }
      storage {
        label
        company {
          name
        }
      }
    }
    status
    timeCreated
    timeProcessed
    currency
    price
    finalPrice
    customTransport
    note
    traderSigned
  }
`);

// eslint-disable-next-line lingui/no-unlocalized-strings
const contractsDocument = graphql(`
  query Contracts($companyId: ID!, $locale: String) {
    contracts(companyId: $companyId) {
      edges {
        node {
          ...ContractFields
        }
      }
    }
  }
`);

const contractsQuery = ({
  companyId,
  locale,
}: {
  companyId: string;
  locale: string;
}) =>
  queryOptions({
    queryFn: () =>
      graphqlClient.request(contractsDocument, {
        companyId,
        locale,
      }),
    queryKey: ["contracts", companyId, locale],
    select: (data) => {
      return data.contracts?.edges
        ?.map((edge) => createSafeContract(edge?.node))
        .toSorted(newestFirst);
    },
  });

// TODO pass currency
const totalSalesQuery = ({
  companyId,
  dateFrom,
  dateTo,
  locale,
}: {
  companyId: string;
  dateFrom: DateTime<true> | undefined;
  dateTo: DateTime<true> | undefined;
  locale: string;
}) =>
  queryOptions({
    ...contractsQuery({ companyId, locale }),
    queryKey: [
      "contracts",
      companyId,
      "successful",
      dateFrom ? dateFrom.toISO()! : "",
      dateTo ? dateTo.toISO()! : "",
    ],
    select: (data) => {
      const sum =
        data.contracts?.edges
          ?.filter((edge) => edge?.node?.status === ContractStatus.Signed)
          .filter((edge) => {
            if (!dateFrom || !dateTo) {
              return true;
            }

            return Interval.fromDateTimes(
              dateFrom,
              dateTo.endOf("day"),
            ).contains(
              DateTime.fromMillis(edge?.node?.timeProcessed * 1000).startOf(
                "day",
              ),
            );
          })
          .reduce((sum, contractPrice) => {
            return (
              sum +
              (contractPrice?.node?.finalPrice ?? 0) *
                (contractPrice?.node?.amount ?? 0)
            );
          }, 0) ?? 0;

      return currencyFormatter("CZK").format(sum);
    },
  });

// eslint-disable-next-line lingui/no-unlocalized-strings
const contractDocument = graphql(`
  query Contract($id: ID!, $locale: String) {
    contract(id: $id) {
      ...ContractFields
      userSignature {
        id
        slug
        status
      }
    }
  }
`);

const contractSchema = z.object({
  amount: z.number(),
  company: z.object({
    name: z.string(),
  }),
  confirmation: z.null().or(
    z.object({
      completed: z.boolean(),
      documents: z.array(
        z.object({
          name: z.string(),
          url: z.string(),
        }),
      ),
      id: z.string(),
    }),
  ),
  crop: z.object({
    name: z.string(),
    unit: cropUnitSchema,
  }),
  currency: z.enum(["CZK", "EUR"]),
  customTransport: z.boolean(),
  externalNumber: z.string().nullable().optional(),
  finalPrice: z.number(),
  id: z.string(),
  note: z.string().nullable(),
  price: z.number(),
  quarterName: z.string().optional(),
  status: z.enum(ContractStatus),
  storage: z.object({
    name: z.string(),
  }),
  timeCreated: z.number().transform((value) => DateTime.fromSeconds(value)),
  timeProcessed: z
    .number()
    .or(z.null())
    .transform((value) => (value ? DateTime.fromSeconds(value) : undefined)),
  trader: traderSchema,
  traderSigned: z.boolean(),
});

const signatureSchema = z
  .object({
    id: z.string(),
    slug: z.string(),
    status: z.union([
      z.literal("COMPLETED"),
      z.literal("AWAITING"),
      z.literal("SENT"),
      z.literal("OPENED"),
    ]),
  })
  .or(z.null());

const contractQuery = ({ id, locale }: { id: string; locale: string }) =>
  queryOptions({
    queryFn: () =>
      graphqlClient.request(contractDocument, {
        id,
        locale,
      }),
    queryKey: ["contract", id, locale],
    select: ({ contract }): ContractQueryResult => {
      const safeContract = createSafeContract(contract);
      const safeSignature = signatureSchema.parse(contract?.userSignature);
      return { ...safeContract, userSignature: safeSignature };
    },
  });

type ContractQueryResult = {
  userSignature: z.infer<typeof signatureSchema>;
} & z.infer<typeof contractSchema>;

const createSafeContract = (maybeContract?: ContractFieldsFragment | null) =>
  contractSchema.parse({
    amount: maybeContract?.amount,
    company: maybeContract?.offer?.storage?.company,
    confirmation: maybeContract?.confirmation ?? null,
    crop: maybeContract?.offer?.crop,
    currency: maybeContract?.currency,
    customTransport: maybeContract?.customTransport,
    externalNumber: maybeContract?.externalNumber,
    finalPrice: maybeContract?.finalPrice,
    id: maybeContract?.id,
    note: maybeContract?.note,
    price: maybeContract?.price,
    quarterName: maybeContract?.marketPrice?.quarter?.name,
    status: maybeContract?.status,
    storage: {
      name: maybeContract?.offer?.storage?.label,
    },
    timeCreated: maybeContract?.timeCreated,
    timeProcessed: maybeContract?.timeProcessed,
    trader: {
      id: maybeContract?.marketPrice?.trader?.id,
      logo: maybeContract?.marketPrice?.trader?.logo,
      name: maybeContract?.marketPrice?.trader?.name,
    },
    traderSigned: maybeContract?.traderSigned,
  });

// eslint-disable-next-line lingui/no-unlocalized-strings
const confirmationDocument = graphql(`
  query Confirmation($id: ID!) {
    contract(id: $id) {
      userSignature {
        id
        slug
        status
      }
      traderSigned
      confirmation {
        id
        completed
        documents {
          name
          url
        }
      }
    }
  }
`);

const safeContractSchema = z
  .object({
    confirmation: z.null().or(
      z.object({
        completed: z.boolean(),
        documents: z.array(
          z.object({
            name: z.string(),
            url: z.string(),
          }),
        ),
        id: z.string(),
      }),
    ),
    docusealSlug: z.string().optional(),
    signedByTrader: z.boolean(),
  })
  .or(z.null());

const confirmationQuery = ({ contractId }: { contractId: string }) =>
  queryOptions({
    queryFn: () =>
      graphqlClient.request(confirmationDocument, {
        id: contractId,
      }),
    queryKey: ["contract", contractId, "confirmation"],
    select: (data) => {
      // FIXME when `signedByTrader` flag is false, neither confirmation nor docusealSlug make much sense here

      return safeContractSchema.parse({
        confirmation: data.contract?.confirmation,
        docusealSlug: data.contract?.userSignature?.slug,
        signedByTrader: data.contract?.traderSigned,
      });
    },
  });

type ConfirmationQueryResult = z.infer<typeof safeContractSchema>;

export { confirmationQuery, contractQuery, contractsQuery, totalSalesQuery };
export type { ConfirmationQueryResult, ContractQueryResult };
